import { create } from "zustand";
import { globalChatApi, type ChatMessage } from "../lib/api";
import { streamChat } from "../lib/sse";
import { useAuthStore } from "./authStore";

/** Get today's date key in YYYY-MM-DD */
function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

interface GlobalChatState {
  messages: ChatMessage[];
  sessions: string[];
  activeDate: string;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;

  // Computed
  isReadOnly: boolean;

  // Actions
  loadSessions: () => Promise<void>;
  selectDate: (date: string) => Promise<void>;
  loadHistory: () => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  clearChat: () => Promise<void>;
  reset: () => void;
}

export const useGlobalChatStore = create<GlobalChatState>((set, get) => ({
  messages: [],
  sessions: [],
  activeDate: todayKey(),
  isLoading: false,
  error: null,
  isInitialized: false,
  isReadOnly: false,

  loadSessions: async () => {
    try {
      const { sessions } = await globalChatApi.getSessions();
      set({ sessions });
    } catch {
      // Non-critical, sessions tab bar just won't show past dates
    }
  },

  selectDate: async (date: string) => {
    const today = todayKey();
    set({
      activeDate: date,
      isReadOnly: date !== today,
      messages: [],
      error: null,
    });

    // Load history for the selected date
    set({ isLoading: true });
    try {
      const { messages } = await globalChatApi.getHistory(date);
      set({ messages, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load chat history";
      set({ isLoading: false, error: message });
    }
  },

  loadHistory: async () => {
    if (get().isLoading) return;

    set({ isLoading: true, error: null });

    try {
      const activeDate = get().activeDate;
      const { messages } = await globalChatApi.getHistory(activeDate);
      set({
        messages,
        isLoading: false,
        isInitialized: true,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load chat history";
      set({
        isLoading: false,
        error: message,
        isInitialized: true,
      });
    }
  },

  sendMessage: async (content: string) => {
    if (get().isLoading || get().isReadOnly) return;

    const token = useAuthStore.getState().session?.access_token;
    if (!token) return;

    // Create user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      timestamp: new Date().toISOString(),
    };

    // Create streaming assistant placeholder
    const streamingId = `streaming-${Date.now()}`;
    const assistantPlaceholder: ChatMessage = {
      id: streamingId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage, assistantPlaceholder],
      isLoading: true,
      error: null,
    }));

    try {
      await streamChat(
        "/chat/global",
        { message: content },
        token,
        {
          onChunk: (text) => {
            set((state) => ({
              messages: state.messages.map((m) =>
                m.id === streamingId
                  ? { ...m, content: m.content + text }
                  : m
              ),
            }));
          },
          onDone: () => {
            set({ isLoading: false });

            // Refresh sessions list (today might be new)
            get().loadSessions();
          },
          onError: (error) => {
            set((state) => ({
              messages: state.messages.filter(
                (m) => m.id !== streamingId && m.id !== userMessage.id
              ),
              isLoading: false,
              error: error.message,
            }));
          },
        },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to send message";
      set((state) => ({
        messages: state.messages.filter(
          (m) => m.id !== streamingId && m.id !== userMessage.id
        ),
        isLoading: false,
        error: message,
      }));
    }
  },

  clearChat: async () => {
    set({ isLoading: true, error: null });

    try {
      const activeDate = get().activeDate;
      await globalChatApi.clearHistory(activeDate);
      set({ messages: [], isLoading: false });

      // Refresh sessions list
      get().loadSessions();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to clear chat";
      set({ isLoading: false, error: message });
    }
  },

  reset: () => {
    set({
      messages: [],
      sessions: [],
      activeDate: todayKey(),
      isLoading: false,
      error: null,
      isInitialized: false,
      isReadOnly: false,
    });
  },
}));
